import * as React from 'react';
import { Accordion, Button, FormItem, Input } from '@rtkwlf/ui';
import { FormGroup, Input as RSInput, Label } from 'reactstrap';
import {
  DARK_PLACEHOLDER,
  getNewValue,
  OnboardingSections,
  ROUTE_PATH,
} from '../../../constants';
import { OnboardingSection } from '../OnboardingSection';
import { useOBAccordionChange } from '../../../hooks';
import { OnboardingAccordionHeader } from '../OnboardingAccordionHeader';
import { OBEndpointConfig, OptionType } from '../../../types';
import { useDispatch, useSelector } from 'react-redux';
import { ONBOARDING_ACTIONS } from '../../../actions/onboarding';
import { apiRequest } from '../../../APIClient';
import {
  DEFAULT_ENDPOINT_CONFIG,
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { sessionSelector } from '../../../reducers/session';
import InputDropdown from '../../../widgets/InputDropdown';
import { ValueType } from 'react-select/src/types';
import styled from 'styled-components';
import { getAccordionHeader } from '../../../utils';
import { Tooltip } from '../../../widgets/Tooltip';

export enum ACCORDION_KEYS {
  SOLUTIONS = 'Endpoint Security Solutions',
  AGENT = 'Endpoint Agent Settings',
}

const ACCORDION_ITEMS: `${ACCORDION_KEYS}`[] = [
  ACCORDION_KEYS.SOLUTIONS,
  ACCORDION_KEYS.AGENT,
];

const EndpointCountContainer = styled.div`
  width: 200px;
  padding-top: 20px;
`;

const PLEASE_SPECIFY = 'Other (please specify)';

const OTHER_EPP_OPTION = { value: 'other', label: PLEASE_SPECIFY };

const toOptionType = (value: string) =>
  ENDPOINT_COUNT_OPTIONS.filter((e) => e.value === value, 10)[0];

const ENDPOINT_COUNT_OPTIONS = [
  {
    value: '0',
    label: 'None',
  },
  {
    value: '250',
    label: 'Up to 250',
  },
  {
    value: '500',
    label: 'Up to 500',
  },
  {
    value: '750',
    label: 'Up to 750',
  },
  {
    value: '1000',
    label: 'Up to 1000',
  },
  {
    value: '2500',
    label: 'Up to 2500',
  },
  {
    value: '5000',
    label: 'Up to 5000',
  },
  {
    value: '7500',
    label: 'Up to 7500',
  },
  {
    value: '10000',
    label: 'Up to 10000',
  },
  {
    value: '10001',
    label: 'Over 10000',
  },
];

export const OnboardingEndpointConfig = () => {
  const config = useSelector(onboardingSelector.getEndpointConfig);
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const availableEndpoints = useSelector(
    memoizedOnboardingSelectors.getAvailableEndpoints,
  );
  const orgID = useSelector(sessionSelector.getRiskID);
  const orgIDParam = orgID ? `?id=${orgID}` : '';

  const hasAvailableEndpoints = React.useMemo(() => {
    return !(
      availableEndpoints.cloudEndpoints.length === 0 ||
      availableEndpoints.onPremiseEndpoints.length === 0
    );
  }, [availableEndpoints]);

  const { collapseAll, isCollapsed, expandAll, expandedItems, handleOnChange } =
    useOBAccordionChange(ACCORDION_ITEMS);

  const dispatch = useDispatch();
  React.useEffect(() => {
    if (config === DEFAULT_ENDPOINT_CONFIG) {
      dispatch(apiRequest.getOBEndpointSection());
    }
    if (!hasAvailableEndpoints) {
      dispatch(apiRequest.getOBAvailableEndpoints());
    }
  }, [dispatch, hasAvailableEndpoints, config]);

  const [availableCloudEpps, setAvailableCloudEpps] = React.useState<
    OptionType[]
  >(
    availableEndpoints.cloudEndpoints.map((aep) => ({
      label: aep.name,
      value: aep.value,
    })),
  );

  const [availableOnPremiseEpps, setAvailableOnPremiseEpps] = React.useState<
    OptionType[]
  >(
    availableEndpoints.onPremiseEndpoints.map((aep) => ({
      label: aep.name,
      value: aep.value,
    })),
  );

  const [cloudEpps, setCloudEpps] = React.useState<OptionType[]>(
    config.cloudEpps?.map((epp) => ({
      value: epp,
      label:
        availableEndpoints?.cloudEndpoints?.find((aep) => aep.value === epp)
          ?.name ?? epp,
    })) ?? [],
  );

  const [onPremEpps, setOnPremEpps] = React.useState<OptionType[]>(
    config.onPremEpps?.map((epp) => ({
      value: epp,
      label:
        availableEndpoints?.onPremiseEndpoints?.find((aep) => aep.value === epp)
          ?.name ?? epp,
    })) ?? [],
  );

  const [endpointNotes, setEndPointNotes] = React.useState(config.notes);
  const [noCloudEpps, setNoCloudEpps] = React.useState(config.noCloudEpps);
  const [noOnPremEpps, setNoOnPremEpps] = React.useState(config.noOnPremEpps);
  const [windowsCount, setWindowsCount] = React.useState('0');
  const [macCount, setMacCount] = React.useState('0');
  const [linuxCount, setLinuxCount] = React.useState('0');
  const [sysmonInstalled, setSysmonInstalled] = React.useState(false);
  const [eppCompleted, setEppCompleted] = React.useState(false);
  const [agentCompleted, setAgentCompleted] = React.useState(false);
  const [noAgents, setNoAgents] = React.useState(false);

  const eppTitle = getAccordionHeader(
    eppCompleted,
    expandedItems,
    ACCORDION_KEYS.SOLUTIONS,
    'Endpoint security solutions complete',
    'Endpoint security solutions incomplete',
  );

  const agentTitle = getAccordionHeader(
    agentCompleted,
    expandedItems,
    ACCORDION_KEYS.AGENT,
    'Endpoint agent settings complete',
    'Endpoint agent settings incomplete',
  );

  const overrideSettings = React.useCallback(
    (overrides: Partial<OBEndpointConfig>) => {
      const config: OBEndpointConfig = {
        linuxEndpoints: linuxCount,
        macEndpoints: macCount,
        windowsEndpoints: windowsCount,
        noCloudEpps,
        noOnPremEpps,
        sysmonInstalled,
        eppCompleted,
        agentCompleted,
        noAgents,
        notes: endpointNotes,
        completed: eppCompleted && (agentCompleted || noAgents),
        onPremEpps: onPremEpps.map((epp) => epp.value),
        cloudEpps: cloudEpps.map((epp) => epp.value),
        finalized: null,
      };
      return {
        ...config,
        ...overrides,
      };
    },
    [
      linuxCount,
      macCount,
      windowsCount,
      noCloudEpps,
      noOnPremEpps,
      sysmonInstalled,
      eppCompleted,
      agentCompleted,
      noAgents,
      endpointNotes,
      onPremEpps,
      cloudEpps,
    ],
  );

  React.useEffect(() => {
    if (config !== DEFAULT_ENDPOINT_CONFIG && hasAvailableEndpoints) {
      setAvailableCloudEpps([
        ...availableEndpoints.cloudEndpoints.map((ep) => ({
          label: ep.name,
          value: ep.value,
        })),
        OTHER_EPP_OPTION,
      ]);
      setAvailableOnPremiseEpps([
        ...availableEndpoints.onPremiseEndpoints.map((ep) => ({
          label: ep.name,
          value: ep.value,
        })),
        OTHER_EPP_OPTION,
      ]);
      setCloudEpps(
        config.cloudEpps?.map((epp) => ({
          value: epp,
          label:
            epp === 'other'
              ? PLEASE_SPECIFY
              : availableEndpoints?.cloudEndpoints?.find(
                  (aep) => aep.value === epp,
                )?.name ?? epp,
        })) ?? [],
      );
      setOnPremEpps(
        config.onPremEpps?.map((epp) => ({
          value: epp,
          label:
            epp === 'other'
              ? PLEASE_SPECIFY
              : availableEndpoints?.onPremiseEndpoints?.find(
                  (aep) => aep.value === epp,
                )?.name ?? epp,
        })) ?? [],
      );
      setMacCount(config.macEndpoints);
      setLinuxCount(config.linuxEndpoints);
      setWindowsCount(config.windowsEndpoints);
      setEndPointNotes(config.notes);
      setNoOnPremEpps(config.noOnPremEpps);
      setNoCloudEpps(config.noCloudEpps);
      setSysmonInstalled(config.sysmonInstalled);
      setEppCompleted(config.eppCompleted);
      setAgentCompleted(config.agentCompleted);
      setNoAgents(config.noAgents);
    }
  }, [availableEndpoints, config, hasAvailableEndpoints]);

  const handleSelectChange = (
    value: ValueType<OptionType>,
    callback: (list: OptionType[]) => void,
    model: keyof Pick<OBEndpointConfig, 'cloudEpps' | 'onPremEpps'>,
  ) => {
    const newValue = getNewValue(value, true) || [];
    callback(newValue);
    dispatch(
      ONBOARDING_ACTIONS.EDIT_ENDPOINT(
        overrideSettings({ [model]: newValue.map((epp) => epp.value) }),
      ),
    );
    dispatch(apiRequest.saveOBEndpointSection());
  };

  const handleNoOnPremEpp = React.useCallback(
    (e) => {
      setNoOnPremEpps(e.currentTarget.checked);
      if (e.currentTarget.checked) {
        setOnPremEpps([]);
      }
      dispatch(
        ONBOARDING_ACTIONS.EDIT_ENDPOINT(
          overrideSettings({
            onPremEpps: e.currentTarget.checked
              ? []
              : onPremEpps.map((epp) => epp.value),
            noOnPremEpps: e.currentTarget.checked,
          }),
        ),
      );
      dispatch(apiRequest.saveOBEndpointSection());
    },
    [dispatch, onPremEpps, overrideSettings],
  );

  const handleNoCloudEpp = React.useCallback(
    (e) => {
      setNoCloudEpps(e.currentTarget.checked);
      if (e.currentTarget.checked) {
        setCloudEpps([]);
      }
      dispatch(
        ONBOARDING_ACTIONS.EDIT_ENDPOINT(
          overrideSettings({
            cloudEpps: e.currentTarget.checked
              ? []
              : cloudEpps.map((epp) => epp.value),
            noCloudEpps: e.currentTarget.checked,
          }),
        ),
      );
      dispatch(apiRequest.saveOBEndpointSection());
    },
    [dispatch, cloudEpps, overrideSettings],
  );
  const handleSysmonInstalled = React.useCallback(
    (e) => {
      setSysmonInstalled(e.currentTarget.checked);
      dispatch(
        ONBOARDING_ACTIONS.EDIT_ENDPOINT(
          overrideSettings({
            sysmonInstalled: e.currentTarget.checked,
          }),
        ),
      );
      dispatch(apiRequest.saveOBEndpointSection());
    },
    [dispatch, setSysmonInstalled, overrideSettings],
  );

  const updateEndpointNotes = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      setEndPointNotes(e.currentTarget.value);
    },
    [setEndPointNotes],
  );

  const endPointNotesBlur = React.useCallback(() => {
    dispatch(
      ONBOARDING_ACTIONS.EDIT_ENDPOINT(
        overrideSettings({
          notes: endpointNotes,
        }),
      ),
    );
    dispatch(apiRequest.saveOBEndpointSection());
  }, [dispatch, endpointNotes, overrideSettings]);

  const endpointCountChange = React.useCallback(
    (
      value: ValueType<OptionType>,
      type: keyof Pick<
        OBEndpointConfig,
        'windowsEndpoints' | 'linuxEndpoints' | 'macEndpoints'
      >,
    ) => {
      const v = getNewValue(value, false);
      const newCount = v?.value ? v.value : '0';
      switch (type) {
        case 'linuxEndpoints':
          setLinuxCount(newCount);
          break;
        case 'macEndpoints':
          setMacCount(newCount);
          break;
        case 'windowsEndpoints':
          setWindowsCount(newCount);
          break;
      }
      dispatch(
        ONBOARDING_ACTIONS.EDIT_ENDPOINT(
          overrideSettings({
            [type]: newCount,
          }),
        ),
      );
      dispatch(apiRequest.saveOBEndpointSection());
    },
    [dispatch, overrideSettings],
  );

  const eppCompletedChange = React.useCallback(() => {
    const newValue = !eppCompleted;
    setEppCompleted(newValue);
    dispatch(
      ONBOARDING_ACTIONS.EDIT_ENDPOINT(
        overrideSettings({
          completed: newValue && agentCompleted,
          eppCompleted: newValue,
        }),
      ),
    );
    dispatch(apiRequest.saveOBEndpointSection());
  }, [
    dispatch,
    overrideSettings,
    agentCompleted,
    setEppCompleted,
    eppCompleted,
  ]);

  const agentCompletedChange = React.useCallback(() => {
    const newValue = !agentCompleted;
    setAgentCompleted(newValue);
    dispatch(
      ONBOARDING_ACTIONS.EDIT_ENDPOINT(
        overrideSettings({
          completed: newValue && eppCompleted,
          agentCompleted: newValue,
        }),
      ),
    );
    dispatch(apiRequest.saveOBEndpointSection());
  }, [
    dispatch,
    overrideSettings,
    agentCompleted,
    setAgentCompleted,
    eppCompleted,
  ]);

  const noAgentsChange = React.useCallback(
    (e) => {
      setNoAgents(e.currentTarget.checked);
      const newAgentCompleted = e.currentTarget.checked ? true : agentCompleted;
      const overrides: Partial<OBEndpointConfig> = {
        noAgents: e.currentTarget.checked,
        completed: newAgentCompleted && eppCompleted,
      };
      if (e.currentTarget.checked) {
        overrides.sysmonInstalled = false;
        overrides.macEndpoints = '0';
        overrides.linuxEndpoints = '0';
        overrides.windowsEndpoints = '0';
        overrides.agentCompleted = true;
        setWindowsCount('0');
        setLinuxCount('0');
        setMacCount('0');
        setSysmonInstalled(false);
      }

      setAgentCompleted(newAgentCompleted);
      dispatch(ONBOARDING_ACTIONS.EDIT_ENDPOINT(overrideSettings(overrides)));
      dispatch(apiRequest.saveOBEndpointSection());
    },
    [
      dispatch,
      overrideSettings,
      agentCompleted,
      setAgentCompleted,
      setNoAgents,
      eppCompleted,
    ],
  );

  return (
    <OnboardingSection
      title={OnboardingSections.ENDPOINT_CONFIG}
      completed={config.completed}
    >
      <OnboardingAccordionHeader
        description="This information helps us to consider all aspects of your endpoint security."
        isCollapsed={isCollapsed}
        collapseAll={collapseAll}
        expandAll={expandAll}
      />
      <div className="ob-accordion-section">
        <Accordion
          itemGap={20}
          expandedItems={expandedItems}
          onChange={handleOnChange}
        >
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.SOLUTIONS}
            header={eppTitle}
            data-cy="endpoint-accordion"
            footer={
              <div className="ob-mark-complete-button mt-2">
                <Button
                  type="primary"
                  data-cy="eppCompleteButton"
                  ghost={eppCompleted}
                  onClick={eppCompletedChange}
                  disabled={!canEdit}
                >
                  {eppCompleted ? 'Mark Incomplete' : 'Mark Complete'}
                </Button>
              </div>
            }
          >
            <p className="ob-section-description">
              Select the endpoint protection platforms (EPP) used by your
              organization.
            </p>
            <FormGroup>
              <h5 className="pb-1">On-Premise Endpoint Security Solution</h5>
              <InputDropdown
                menuPortalTarget={document.body}
                id="onPremEpp"
                isCreatable={false}
                isMulti={true}
                showDropdownArrow={true}
                onChange={(value) => {
                  handleSelectChange(value, setOnPremEpps, 'onPremEpps');
                }}
                value={onPremEpps}
                options={availableOnPremiseEpps}
                placeholder="Choose a On-Premise Endpoint Security Solution"
                styles={DARK_PLACEHOLDER}
                isDisabled={!canEdit || noOnPremEpps}
                classNamePrefix="onPremEpp"
              />
              <Label className="ob-accordion-checkbox" check>
                <RSInput
                  type="checkbox"
                  id="noOnPremEpp"
                  data-cy="noOnPremEpp"
                  disabled={!canEdit}
                  onChange={handleNoOnPremEpp}
                  checked={noOnPremEpps}
                />
                I don't have an on-premise security solution
              </Label>
            </FormGroup>
            <FormGroup>
              <h5 className="pb-1">Cloud Endpoint Security Solution</h5>
              <InputDropdown
                menuPortalTarget={document.body}
                id="cloudEpp"
                isCreatable={false}
                isMulti={true}
                showDropdownArrow={true}
                onChange={(value) => {
                  handleSelectChange(value, setCloudEpps, 'cloudEpps');
                }}
                value={cloudEpps}
                options={availableCloudEpps}
                placeholder="Choose a Cloud Endpoint Security Solution"
                styles={DARK_PLACEHOLDER}
                isDisabled={!canEdit || noCloudEpps}
                classNamePrefix="cloudEpp"
              />
              <div>
                <span style={{ fontSize: '11px' }}>
                  Include additional details in the endpoint notes
                </span>
              </div>
              <Label className="ob-accordion-checkbox" check>
                <RSInput
                  type="checkbox"
                  id="noCloudEpp"
                  data-cy="noCloudEpp"
                  disabled={!canEdit}
                  onChange={handleNoCloudEpp}
                  checked={noCloudEpps}
                />
                I don't have a cloud EPP
              </Label>
            </FormGroup>
            <FormGroup>
              <div className="d-flex align-items-center">
                <h5 className="pb-1 mr-2">Endpoint Notes</h5>
                <Tooltip
                  style={{ paddingBottom: '5px' }}
                  text="Provide additional endpoint details such as unsupported vendors and site-specific endpoint security solutions."
                  placement="bottom"
                />
              </div>
              <FormItem hasIcon={false} data-cy="endPointNotes">
                <Input
                  disabled={!canEdit}
                  htmlType="textarea"
                  id="endpointNotes"
                  value={endpointNotes}
                  onChange={updateEndpointNotes}
                  onBlur={endPointNotesBlur}
                />
              </FormItem>
            </FormGroup>
          </Accordion.Item>
          <Accordion.Item
            className="mr-3"
            itemKey={ACCORDION_KEYS.AGENT}
            header={agentTitle}
            footer={
              <div className="d-flex justify-content-between">
                <div className="pt-2">
                  <Label className="ob-accordion-checkbox" check>
                    <RSInput
                      type="checkbox"
                      id="noAgents"
                      disabled={!canEdit}
                      onChange={noAgentsChange}
                      checked={noAgents}
                    />
                    I don't want to use the endpoint agent
                  </Label>
                </div>
                <div className="ob-mark-complete-button mt-2">
                  <Button
                    type="primary"
                    data-cy="agentCompleteButton"
                    ghost={agentCompleted}
                    onClick={agentCompletedChange}
                    disabled={!canEdit || noAgents}
                  >
                    {agentCompleted ? 'Mark Incomplete' : 'Mark Complete'}
                  </Button>
                </div>
              </div>
            }
          >
            <div className="pl-2">
              <p className="ob-section-description">
                Estimate the number of endpoint devices that will install the
                Arctic Wolf Agent. Endpoint agents gather intelligence about the
                endpoints they are installed on.
              </p>
              <h5 className="pb-1">Windows Endpoints</h5>
              <div style={{ width: '200px' }}>
                <InputDropdown
                  id="windowsEndpoints"
                  menuPortalTarget={document.body}
                  isCreatable={false}
                  isMulti={false}
                  showDropdownArrow={true}
                  onChange={(value) =>
                    endpointCountChange(value, 'windowsEndpoints')
                  }
                  options={ENDPOINT_COUNT_OPTIONS}
                  placeholder="None"
                  styles={DARK_PLACEHOLDER}
                  value={toOptionType(windowsCount)}
                  isDisabled={noAgents || !canEdit}
                  classNamePrefix="windowsEndpoints"
                />
              </div>
              <Label className="ob-accordion-checkbox" check>
                <RSInput
                  type="checkbox"
                  id="sysmonInstalled"
                  disabled={!canEdit || noAgents}
                  onChange={handleSysmonInstalled}
                  checked={sysmonInstalled}
                />
                Sysmon will be installed on all Windows endpoints (required for
                full coverage)
              </Label>
              <EndpointCountContainer>
                <h5 className="pb-1">Mac Endpoints</h5>
                <InputDropdown
                  id="macEndpoints"
                  menuPortalTarget={document.body}
                  isCreatable={false}
                  isMulti={false}
                  showDropdownArrow={true}
                  onChange={(value) =>
                    endpointCountChange(value, 'macEndpoints')
                  }
                  options={ENDPOINT_COUNT_OPTIONS}
                  placeholder="None"
                  styles={DARK_PLACEHOLDER}
                  value={toOptionType(macCount)}
                  isDisabled={noAgents || !canEdit}
                  classNamePrefix="macEndpoints"
                />
              </EndpointCountContainer>
              <EndpointCountContainer>
                <h5 className="pb-1">Linux Endpoints</h5>
                <InputDropdown
                  id="linuxEndpoints"
                  menuPortalTarget={document.body}
                  isCreatable={false}
                  isMulti={false}
                  showDropdownArrow={true}
                  onChange={(value) =>
                    endpointCountChange(value, 'linuxEndpoints')
                  }
                  options={ENDPOINT_COUNT_OPTIONS}
                  placeholder="None"
                  styles={DARK_PLACEHOLDER}
                  value={toOptionType(linuxCount)}
                  isDisabled={noAgents || !canEdit}
                  classNamePrefix="linuxEndpoints"
                />
              </EndpointCountContainer>
              <div className="pt-3">
                Go to{' '}
                <a
                  href={ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL + orgIDParam}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Downloads
                </a>{' '}
                to begin downloading the endpoint agent.
              </div>
            </div>
          </Accordion.Item>
        </Accordion>
      </div>
    </OnboardingSection>
  );
};

export default OnboardingEndpointConfig;
